<div class="min-h-screen bg-gray-50 text-gray-800 flex flex-col">
  <!-- Header bar -->
  <header class="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3">
    <button
      class="text-primary hover:underline text-sm flex items-center whitespace-nowrap"
      (click)="location.back()"
    >
      <i class="ri-arrow-go-back-fill mr-1"></i>
      Back
    </button>
    <span class="text-sm text-gray-500 font-mono break-all">
      /diff?a={{ sessionAValue }}&amp;b={{ sessionBValue }}
    </span>
    <div class="flex-1"></div>
    <button
      class="btn btn-xs btn-outline"
      (click)="swap()"
      [disabled]="!diff"
      title="Swap which session is A and which is B"
    >
      <i class="ri-arrow-left-right-line mr-1"></i>
      Swap A ↔ B
    </button>
    <button
      class="btn btn-xs btn-outline"
      (click)="exportJson()"
      [disabled]="!diff"
      title="Download the computed diff as JSON"
    >
      <i class="ri-download-line mr-1"></i>
      Export JSON
    </button>
  </header>

  <ngx-spinner type="ball-clip-rotate" size="medium"></ngx-spinner>

  <div class="flex-1 max-w-6xl mx-auto w-full px-4 py-4 space-y-4">
    @if (loading) {
      <div class="flex items-center gap-2 text-sm text-gray-500 px-1 py-2">
        <i class="ri-loader-4-line animate-spin"></i>
        Computing diff…
      </div>
    }

    @if (errorMsg) {
      <div class="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
        <div class="flex items-center">
          <i class="ri-error-warning-line mr-1"></i>
          {{ errorMsg }}
        </div>
        @if (sessionAValue && sessionBValue && sessionAValue !== sessionBValue) {
          <button
            class="btn btn-xs btn-outline mt-2"
            (click)="runDiff()"
            [disabled]="loading"
            title="Re-run the diff for these two sessions"
          >
            <i class="ri-refresh-line mr-1"></i>
            Try again
          </button>
        }
      </div>
    }

    @if (diff) {
      <!-- SUMMARY BAND -->
      <section class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
          <i class="ri-git-compare-line text-gray-500"></i>
          <h2 class="text-sm font-semibold uppercase tracking-wider text-gray-600">Summary</h2>
          <span class="ml-auto text-xs text-gray-500">
            Compared in {{ computeTime() }} · {{ pairedCount() }} paired · {{ diff.unpaired }}
            unpaired
          </span>
        </div>
        <div class="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
          <div class="p-4">
            <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Session A</div>
            <a
              [routerLink]="['/search']"
              [queryParams]="{ q: sessionAValue }"
              class="font-mono text-sm text-primary break-all hover:underline"
            >
              {{ sessionAValue }}
            </a>
            <div class="text-xs text-gray-500 mt-1">{{ sessionMeta('a') }}</div>
          </div>
          <div class="p-4">
            <div class="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Session B</div>
            <a
              [routerLink]="['/search']"
              [queryParams]="{ q: sessionBValue }"
              class="font-mono text-sm text-primary break-all hover:underline"
            >
              {{ sessionBValue }}
            </a>
            <div class="text-xs text-gray-500 mt-1">{{ sessionMeta('b') }}</div>
          </div>
        </div>

        <div class="grid md:grid-cols-4 divide-x divide-gray-200 border-t border-gray-200">
          <div class="p-4 text-center">
            <div class="text-2xl font-bold text-gray-900 tabular-nums">{{ diff.totalDiffs }}</div>
            <div class="text-xs text-gray-500 mt-0.5">Total differences</div>
          </div>
          <div class="p-4 text-center">
            <div class="text-2xl font-bold text-gray-900 tabular-nums">
              {{ diff.pairedWithDiffs }} / {{ pairedCount() }}
            </div>
            <div class="text-xs text-gray-500 mt-0.5">Paired requests with diffs</div>
          </div>
          <div class="p-4 text-center">
            <div
              class="text-2xl font-bold tabular-nums"
              [class.text-amber-700]="diff.unpaired > 0"
              [class.text-gray-500]="diff.unpaired === 0"
            >
              {{ diff.unpaired }}
            </div>
            <div class="text-xs text-gray-500 mt-0.5">Unpaired requests</div>
          </div>
          <div class="p-4 text-center">
            <div class="text-2xl font-bold text-gray-500 tabular-nums">
              {{ diff.totalExcluded }}
            </div>
            <div class="text-xs text-gray-500 mt-0.5">
              Excluded as dynamic
              <button
                class="text-primary hover:underline ml-1"
                (click)="showExcluded = !showExcluded"
                [disabled]="diff.totalExcluded === 0"
              >
                {{ showExcluded ? 'hide' : 'view' }}
              </button>
            </div>
          </div>
        </div>

        @if (showExcluded) {
          <div class="border-t border-gray-200 max-h-64 overflow-auto bg-gray-50">
            <table class="w-full text-xs">
              <thead class="bg-gray-100 sticky top-0">
                <tr>
                  <th class="px-3 py-1.5 text-left text-gray-600 font-semibold">Request</th>
                  <th class="px-3 py-1.5 text-left text-gray-600 font-semibold">Payload</th>
                  <th class="px-3 py-1.5 text-left text-gray-600 font-semibold">Path</th>
                  <th class="px-3 py-1.5 text-left text-gray-600 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                @for (row of allExcluded; track $index) {
                  <tr class="border-t border-gray-200">
                    <td class="px-3 py-1 text-gray-700">{{ row.pairLabel }}</td>
                    <td class="px-3 py-1 text-gray-700">{{ row.payloadName }}</td>
                    <td class="px-3 py-1 font-mono text-gray-600">{{ row.leaf.path }}</td>
                    <td class="px-3 py-1 text-gray-500">{{ row.leaf.reason }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <!-- PER-REQUEST CARDS -->
      @for (pair of diff.pairs; track $index; let i = $index) {
        <section class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div
            class="px-4 py-2.5 border-b border-gray-200 flex items-center gap-3"
            [class.bg-amber-50]="pair.status === 'paired' && (pair.totalDiffs ?? 0) > 0"
            [class.bg-blue-50]="pair.status !== 'paired'"
          >
            @if (pair.status === 'paired' && (pair.totalDiffs ?? 0) > 0) {
              <span
                class="text-xs font-semibold uppercase tracking-wider text-amber-700 px-2 py-0.5 rounded bg-amber-100 border border-amber-200"
              >
                {{ pair.totalDiffs }} diff{{ pair.totalDiffs === 1 ? '' : 's' }}
              </span>
            } @else if (pair.status === 'paired') {
              <span
                class="text-xs font-semibold uppercase tracking-wider text-green-700 px-2 py-0.5 rounded bg-green-100 border border-green-200"
              >
                identical
              </span>
            } @else {
              <span
                class="text-xs font-semibold uppercase tracking-wider text-blue-700 px-2 py-0.5 rounded bg-blue-100 border border-blue-200"
              >
                {{ pair.status === 'only_a' ? 'only in A' : 'only in B' }}
              </span>
            }
            <div class="flex items-center gap-1.5 text-sm">
              <span class="text-gray-500">[{{ i }}]</span>
              <span class="font-medium text-gray-900">{{ pairLabel(pair) }}</span>
            </div>
            <span class="text-xs text-gray-400 ml-2">{{ pairPositionHint(pair, i) }}</span>
            @if (pair.status === 'paired' && (pair.totalExcluded ?? 0) > 0) {
              <span class="ml-auto text-xs text-gray-400">
                {{ pair.totalExcluded }} excluded as dynamic
              </span>
            }
          </div>

          @if (pair.status === 'paired' && pair.payloadDiffs) {
            <div class="p-3 space-y-3">
              @for (payload of pair.payloadDiffs; track payload.messageName) {
                @if (
                  payload.diffs.length > 0 ||
                  payload.onlyOn ||
                  payload.unparseable ||
                  payload.skippedNonJson ||
                  payload.skippedTooLarge
                ) {
                  <div>
                    <div
                      class="text-[11px] uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-2"
                    >
                      <i class="ri-arrow-right-line"></i>
                      {{ payload.messageName }}
                      @if (payload.format) {
                        <span class="text-gray-400">— {{ payload.format }}</span>
                      }
                    </div>

                    @if (payload.diffs.length > 0) {
                      <ul
                        class="text-sm divide-y divide-gray-100 border border-gray-200 rounded overflow-hidden"
                      >
                        @for (leaf of payload.diffs; track leaf.path) {
                          <li class="grid grid-cols-12 gap-2 px-3 py-2 hover:bg-amber-50/40">
                            <div class="col-span-5 font-mono text-xs text-gray-600 break-all">
                              {{ leaf.path }}
                            </div>
                            <div class="col-span-3 font-mono text-xs break-all">
                              <span class="bg-red-50 text-red-800 px-1 rounded">
                                {{ formatValue(leaf.leftValue) }}
                              </span>
                            </div>
                            <div class="col-span-3 font-mono text-xs break-all">
                              <span class="bg-green-50 text-green-800 px-1 rounded">
                                {{ formatValue(leaf.rightValue) }}
                              </span>
                            </div>
                            <div class="col-span-1 text-right">
                              <a
                                [routerLink]="['/search']"
                                [queryParams]="diffOpenQueryParams(pair, 'a', payload, leaf.path)"
                                class="text-primary hover:underline text-xs inline-flex items-center gap-0.5"
                                title="Open this payload in the request view, highlighted"
                              >
                                open
                                <i class="ri-external-link-line"></i>
                              </a>
                            </div>
                          </li>
                        }
                      </ul>
                    } @else if (payload.onlyOn) {
                      <div
                        class="text-xs text-gray-500 italic border border-gray-200 rounded px-3 py-2 bg-gray-50"
                      >
                        Message only present in side
                        {{ payload.onlyOn === 'a' ? 'A' : 'B' }} — nothing to compare.
                      </div>
                    } @else if (payload.skippedNonJson) {
                      <div
                        class="text-xs text-gray-500 italic border border-gray-200 rounded px-3 py-2 bg-gray-50"
                      >
                        Skipped — format is
                        <code class="font-mono">{{ payload.format }}</code>
                        (only JSON payloads are diffed in this version).
                      </div>
                    } @else if (payload.skippedTooLarge) {
                      <div
                        class="text-xs text-gray-500 italic border border-gray-200 rounded px-3 py-2 bg-gray-50"
                      >
                        Skipped — body too large to fetch inline ({{ payload.note }}).
                      </div>
                    } @else if (payload.unparseable) {
                      <div
                        class="text-xs text-red-700 italic border border-red-200 rounded px-3 py-2 bg-red-50"
                      >
                        Could not parse one of the bodies as JSON: {{ payload.note }}
                      </div>
                    }
                  </div>
                }
              }

              @if (pair.totalDiffs === 0 && (pair.totalExcluded ?? 0) > 0) {
                <div
                  class="text-xs text-gray-500 italic border border-gray-200 rounded px-3 py-2 bg-gray-50"
                >
                  No actionable differences — every leaf delta is either an ID, a timestamp, or in
                  the excluded paths list. ({{ pair.totalExcluded }} excluded; click "view" in the
                  summary above to inspect.)
                </div>
              }
            </div>
          } @else {
            <div class="p-3 text-xs text-gray-500 italic">
              @if (pair.status === 'only_a') {
                Session B has no
                <code class="font-mono">{{ pair.a?.method }}</code>
                counterpart.
              } @else {
                Session A has no
                <code class="font-mono">{{ pair.b?.method }}</code>
                counterpart.
              }
            </div>
          }
        </section>
      }
    }
  </div>
</div>
